import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Block =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'qa'; q: string; a: string }
  | { type: 'cta'; text: string; to?: string; href?: string; external?: boolean };

export function ContentSections({ blocks }: { blocks: Block[] }) {
  return (
    <div className="dx-content-sections space-y-6">
      {blocks.map((block, i) => {
        if (block.type === 'p') {
          return (
            <p key={i} className="text-neutral-medium font-body leading-relaxed">
              {block.text}
            </p>
          );
        }
        if (block.type === 'h2') {
          return (
            <h2 key={i} className="dx-content-h2 text-xl md:text-2xl font-heading font-semibold text-brand-accent pt-2">
              {block.text}
            </h2>
          );
        }
        if (block.type === 'h3') {
          return (
            <h3 key={i} className="text-lg font-heading font-semibold text-brand-accent">
              {block.text}
            </h3>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const Tag = block.type === 'ul' ? 'ul' : 'ol';
          return (
            <Tag
              key={i}
              className={`text-neutral-medium font-body leading-relaxed space-y-2 ${
                block.type === 'ul' ? 'list-disc' : 'list-decimal'
              } ps-5`}
            >
              {block.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Tag>
          );
        }
        if (block.type === 'qa') {
          return (
            <div key={i} className="rounded-xl border border-neutral-dark/40 bg-neutral-dark/10 p-4 md:p-5">
              <h3 className="font-heading font-semibold text-brand-accent mb-2">{block.q}</h3>
              <p className="text-neutral-medium font-body leading-relaxed m-0">{block.a}</p>
            </div>
          );
        }
        if (block.type === 'cta') {
          const className =
            'inline-flex items-center justify-center h-11 px-5 rounded-full bg-brand-accent text-white font-heading font-semibold text-sm hover:opacity-95 transition';
          if (block.href) {
            return (
              <a
                key={i}
                href={block.href}
                target={block.external ? '_blank' : undefined}
                rel={block.external ? 'noreferrer' : undefined}
                className={className}
              >
                {block.text}
              </a>
            );
          }
          return (
            <Link key={i} to={block.to || '/'} className={className}>
              {block.text}
            </Link>
          );
        }
        return null;
      })}
    </div>
  );
}

export function useContentBlocks(key: string): Block[] {
  const { t } = useTranslation();
  const raw = t(key, { returnObjects: true });
  return Array.isArray(raw) ? (raw as Block[]) : [];
}
